import React, { useEffect, useRef, useState, ChangeEvent } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FaCheckCircle,
  FaExclamationCircle,
  FaBuilding,
  FaSpinner,
  FaTrashAlt,
  FaArrowLeft,
  FaPlus,
  FaTimes,
  FaSignature,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaWhatsapp,
} from "react-icons/fa";

import { locationMaster } from "../utils/locationMaster";

// --- Config
const API_URL = process.env.REACT_APP_API_URL!;

// --- State & Field Configs
const initialCompanyState = {
  company_name: "",
  address: "",
  pancard: "",
  city: "",
  state: "",
  country: "",
  gst_number: "",
  gst_uin: "",
  code: "",
  cin: "",
  email: "",
  phone: "",
  password: "",
  HSN_SAC: "[]",
  GST_SLAB: "[]",
  signature: "",
  prefix: "",
  postfix: "",
  separatorsign: "",
  subscription_plan_id: "",
  subscription_start_date: "",
  subscription_status: "active",
  whatsapp_api_url: "",
  whatsapp_api_token: "",
};

type CompanyType = typeof initialCompanyState & { id?: string | number; bank_id?: string | number };

const COLOR_GRADIENT = "bg-gradient-to-br from-sky-50 via-pink-50 to-indigo-50";

// Field Groups for organized multi-row modern layout
const fieldGroups: Array<{
  title?: string;
  icon?: any;
  fields: Array<any>;
  cols?: string; // grid-cols-N for responsive grouping
}> = [
  {
    title: "Basic Info",
    icon: FaBuilding,
    cols: "md:grid-cols-2",
    fields: [
      {
        label: "Company Name",
        name: "company_name",
        required: true,
        icon: <FaBuilding className="inline mr-2 text-sky-700" />,
      },
      { label: "Address", name: "address", textarea: true, areaRows: 3, gridFull: true },
      { label: "Email", name: "email", inputType: "email" },
      { label: "Phone / WhatsApp Number", name: "phone", inputType: "tel" },
      {
        label: "Password",
        name: "password",
        inputType: "password",
        icon: <FaLock className="inline text-sky-700 mr-2" />,
        showPasswordToggle: true, // add a prop for FormField
      },
      { label: "City", name: "city" },
      { label: "State", name: "state" },
      { label: "Country", name: "country" },
    ],
  },
  {
    title: "Registration",
    cols: "md:grid-cols-3",
    fields: [
      { label: "PAN", name: "pancard" },
      { label: "GST Number", name: "gst_number" },
      { label: "GST UIN", name: "gst_uin" },
      { label: "Code", name: "code" },
      { label: "CIN", name: "cin" },
    ],
  },
  {
    title: "WhatsApp Business API (WABA) Settings",
    icon: FaWhatsapp,
    cols: "md:grid-cols-2",
    fields: [
      { label: "WhatsApp API URL", name: "whatsapp_api_url" },
      { label: "WhatsApp API Token", name: "whatsapp_api_token" },
    ],
  },
];

// --- Separator Options
const separatorOptions = [
  { label: "-", value: "-" },
  { label: "/", value: "/" },
  { label: "_", value: "_" },
  { label: ".", value: "." },
  { label: "|", value: "|" },
];

// --- AddCompany Component
const AddCompany: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyId = searchParams.get("company_id");
  const isEditMode = companyId && companyId !== "new";

  // --- States
  const [company, setCompany] = useState<CompanyType>(initialCompanyState);
  const [subscriptionPlans, setSubscriptionPlans] = useState<any[]>([]);
  const [createAdminUser, setCreateAdminUser] = useState(false);
  const [adminUsername, setAdminUsername] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminPassword, setAdminPassword] = useState("");

  const [hsnSacs, setHsnSacs] = useState<string[]>([]);
  const [gstSlabs, setGstSlabs] = useState<string[]>([]);
  const [hsnInput, setHsnInput] = useState<string>("");
  const [hsnDescInput, setHsnDescInput] = useState<string>("");
  const [gstInput, setGstInput] = useState<string>("");
  const [gstDescInput, setGstDescInput] = useState<string>("");
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string>("");
  const [letterheadFile, setLetterheadFile] = useState<File | null>(null);
  const [letterheadPreview, setLetterheadPreview] = useState<string>("");
  const [prefixInput, setPrefixInput] = useState<string>("");
  const [postfixInput, setPostfixInput] = useState<string>("");

  // separator state
  const [separatorInput, setSeparatorInput] = useState<string>("");

  const handleLetterheadChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.match(/^image\/(png|jpeg|jpg|webp|svg\+xml)$/)) {
      setFormError(
        "Please upload an image file (png, jpg, jpeg, svg, webp) for the letterhead."
      );
      return;
    }
    setLetterheadFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result && typeof reader.result === "string") {
        setLetterheadPreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearLetterhead = () => {
    setLetterheadPreview("");
    setLetterheadFile(null);
  };

  useEffect(() => {
    axios
      .get(`${API_URL}/auth/subscription-plans`)
      .then((res) => {
        setSubscriptionPlans(Array.isArray(res.data) ? res.data : []);
      })
      .catch((err) => {
        console.error("Failed to fetch subscription plans:", err);
      });
  }, []);

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string>("");
  const [formError, setFormError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // --- Password field: show/hide
  const [showPassword, setShowPassword] = useState(false);

  const alertRef = useRef<HTMLDivElement>(null);

  // --- Scroll to feedback
  useEffect(() => {
    if (error || formError || success) {
      alertRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [error, formError, success]);

  // --- Fetch details for edit mode
  useEffect(() => {
    if (isEditMode && companyId) {
      setLoading(true);
      axios
        .get(`${API_URL}/auth/company/${companyId}`)
        .then((res) => {
          setCompany({
            ...res.data,
            // Show password from API if present (even if not editable by user).
            // If API gave empty password or null, default to empty string, so input is always populated (even if empty)
            password: typeof res.data.password !== "undefined" ? res.data.password : "",
            subscription_plan_id: res.data.subscription_plan_id || "",
            subscription_start_date: res.data.subscription_start_date ? res.data.subscription_start_date.substring(0, 10) : "",
            subscription_status: res.data.subscription_status || "active",
          });

          // HSN_SAC: each entry is a string like "12345 - Service/Item description"
          try {
            let loadedHsnSacs: string[] = Array.isArray(res.data.HSN_SAC)
              ? res.data.HSN_SAC
              : JSON.parse(res.data.HSN_SAC || "[]");
            setHsnSacs(loadedHsnSacs);
          } catch {
            setHsnSacs([]);
          }

          // GST_SLAB: each entry is a string like "18 - Textile GST"
          try {
            let loadedGstSlabs: string[] = Array.isArray(res.data.GST_SLAB)
              ? res.data.GST_SLAB
              : JSON.parse(res.data.GST_SLAB || "[]");
            setGstSlabs(loadedGstSlabs);
          } catch {
            setGstSlabs([]);
          }
          if (res.data.signature) {
            if (
              typeof res.data.signature === "string" &&
              res.data.signature?.trim() !== "" &&
              !res.data.signature.startsWith("http") &&
              !res.data.signature.startsWith("data:")
            ) {
              let url = res.data.signature;
              if (url.startsWith("/")) url = url.substring(1);
              let apiBase = API_URL || "";
              if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
              setSignaturePreview(`${apiBase}/${url}`);
            } else {
              setSignaturePreview(res.data.signature);
            }
          } else {
            setSignaturePreview("");
          }
          setSignatureFile(null);

          if (res.data.letterhead) {
            if (
              typeof res.data.letterhead === "string" &&
              res.data.letterhead?.trim() !== "" &&
              !res.data.letterhead.startsWith("http") &&
              !res.data.letterhead.startsWith("data:")
            ) {
              let url = res.data.letterhead;
              if (url.startsWith("/")) url = url.substring(1);
              let apiBase = API_URL || "";
              if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
              setLetterheadPreview(`${apiBase}/${url}`);
            } else {
              setLetterheadPreview(res.data.letterhead);
            }
          } else {
            setLetterheadPreview("");
          }
          setLetterheadFile(null);

          // Set prefix and postfix and separator from API for edit mode
          setPrefixInput(res.data.prefix || "");
          setPostfixInput(res.data.postfix || "");
          setSeparatorInput(res.data.separatorsign || "");
        })
        .catch(() => setError("Unable to load company details."))
        .finally(() => setLoading(false));
    } else {
      setCompany(initialCompanyState);
      setHsnSacs([]);
      setGstSlabs([]);
      setSignaturePreview("");
      setSignatureFile(null);
      setLetterheadPreview("");
      setLetterheadFile(null);
      setPrefixInput("");
      setPostfixInput("");
      setSeparatorInput("");
    }
    // eslint-disable-next-line
  }, [companyId, isEditMode]);

  // --- Handlers
  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setCompany((prev) => {
      const updated = { ...prev, [name]: value };
      if (name === "country") {
        updated.state = "";
        updated.city = "";
      } else if (name === "state") {
        updated.city = "";
      }
      return updated;
    });
  };

  const handleSignatureChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (!file.type.match(/^image\/(png|jpeg|jpg|webp|svg\+xml)$/)) {
      setFormError(
        "Please upload an image file (png, jpg, jpeg, svg, webp)."
      );
      return;
    }
    setSignatureFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result && typeof reader.result === "string") {
        setSignaturePreview(reader.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const clearSignature = () => {
    setSignaturePreview("");
    setSignatureFile(null);
  };

  // Handlers for separator
  const handleSeparatorInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let val = e.target.value;
    // Ensure only allowed values and single character
    if (
      !["-", "/", "_", ".", "|"].includes(val) &&
      val.length > 0
    ) {
      setFormError("Choose a valid separator: -, /, _, ., |");
      return;
    }
    setFormError(""); // Clear error if fixing
    setSeparatorInput(val);
  };

  const handleSeparatorSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSeparatorInput(e.target.value);
    setFormError(""); // Clear any separator warnings
  };

  // --- HSN/SAC Section
  const handleHsnInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setHsnInput(e.target.value);
  const handleHsnDescInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setHsnDescInput(e.target.value);
  const handleGstInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setGstInput(e.target.value);
  const handleGstDescInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setGstDescInput(e.target.value);

  const handlePrefixInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setPrefixInput(e.target.value);
  const handlePostfixInputChange = (e: React.ChangeEvent<HTMLInputElement>) => setPostfixInput(e.target.value);

  const addHsnSac = () => {
    if (hsnInput.trim()) {
      let val = hsnInput.trim();
      if (hsnDescInput.trim()) val = `${val} - ${hsnDescInput.trim()}`;
      if (!hsnSacs.includes(val)) {
        setHsnSacs((prev) => [...prev, val]);
      }
      setHsnInput("");
      setHsnDescInput("");
    }
  };
  const removeHsnSac = (val: string) => setHsnSacs((prev) => prev.filter((hsn) => hsn !== val));

  // --- GST SLAB Section
  const addGstSlab = () => {
    if (gstInput.trim()) {
      let val = gstInput.trim();
      if (gstDescInput.trim()) val = `${val} - ${gstDescInput.trim()}`;
      if (!gstSlabs.includes(val)) {
        setGstSlabs((prev) => [...prev, val]);
      }
      setGstInput("");
      setGstDescInput("");
    }
  };
  const removeGstSlab = (val: string) => setGstSlabs((prev) => prev.filter((gst) => gst !== val));

  // --- Validation
  const validateEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // --- Submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    setSuccess("");
    if (!company.company_name.trim()) {
      setFormError("Company name is required.");
      return;
    }
    if (company.email && !validateEmail(company.email)) {
      setFormError("Invalid email address.");
      return;
    }
    if (!separatorInput || !["-", "/", "_", ".", "|"].includes(separatorInput)) {
      setFormError("Please select a valid separator for Invoice No. (e.g. -, /, _, ., | )");
      return;
    }
    setSubmitting(true);
    try {
      let response: any;
      const formData = new FormData();
      formData.append("company_name", company.company_name);
      formData.append("address", company.address);
      formData.append("pancard", company.pancard);
      formData.append("city", company.city);
      formData.append("state", company.state);
      formData.append("country", company.country);
      formData.append("gst_number", company.gst_number);
      formData.append("gst_uin", company.gst_uin);
      formData.append("code", company.code);
      formData.append("cin", company.cin);
      formData.append("email", company.email);
      formData.append("phone", company.phone || "");
      formData.append("password", company.password);

      // Send hsnSacs and gstSlabs as arrays of strings (with - separator)
      formData.append("HSN_SAC", JSON.stringify(hsnSacs));
      formData.append("GST_SLAB", JSON.stringify(gstSlabs));
      if (signatureFile) formData.append("signature", signatureFile);
      if (letterheadFile) formData.append("letterhead", letterheadFile);

      // Add prefix, postfix and separator to formData
      formData.append("prefix", prefixInput || "");
      formData.append("postfix", postfixInput || "");
      formData.append("separatorsign", separatorInput || "");

      // Add subscription details
      formData.append("subscription_plan_id", company.subscription_plan_id || "");
      formData.append("subscription_start_date", company.subscription_start_date || "");
      formData.append("subscription_status", company.subscription_status || "active");

      // Add WhatsApp Business API settings
      formData.append("whatsapp_api_url", company.whatsapp_api_url || "");
      formData.append("whatsapp_api_token", company.whatsapp_api_token || "");

      // Add optional admin user fields
      if (!isEditMode && createAdminUser) {
        if (!adminUsername.trim() || !adminEmail.trim() || !adminPassword.trim()) {
          setFormError("Please fill in all admin user fields (username, email, password).");
          setSubmitting(false);
          return;
        }
        formData.append("admin_username", adminUsername);
        formData.append("admin_email", adminEmail);
        formData.append("admin_password", adminPassword);
      }

      if (isEditMode && companyId) {
        response = await axios.put(
          `${API_URL}/auth/company/${companyId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );
        setSuccess("Company updated successfully!");
        if (response.data.signature) {
          if (
            typeof response.data.signature === "string" &&
            response.data.signature.trim() !== "" &&
            !response.data.signature.startsWith("http") &&
            !response.data.signature.startsWith("data:")
          ) {
            let url = response.data.signature;
            if (url.startsWith("/")) url = url.substring(1);
            let apiBase = API_URL || "";
            if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
            setSignaturePreview(`${apiBase}/${url}`);
          } else setSignaturePreview(response.data.signature);
        }
        if (response.data.letterhead) {
          if (
            typeof response.data.letterhead === "string" &&
            response.data.letterhead.trim() !== "" &&
            !response.data.letterhead.startsWith("http") &&
            !response.data.letterhead.startsWith("data:")
          ) {
            let url = response.data.letterhead;
            if (url.startsWith("/")) url = url.substring(1);
            let apiBase = API_URL || "";
            if (apiBase.endsWith("/")) apiBase = apiBase.slice(0, -1);
            setLetterheadPreview(`${apiBase}/${url}`);
          } else setLetterheadPreview(response.data.letterhead);
        }
      } else {
        response = await axios.post(
          `${API_URL}/auth/company`,
          formData,
          { headers: { "Content-Type": "multipart/form-data" } }
        );
        setSuccess("Company added successfully!");
        setCompany(initialCompanyState);
        setHsnSacs([]);
        setGstSlabs([]);
        setSignaturePreview("");
        setSignatureFile(null);
        setLetterheadPreview("");
        setLetterheadFile(null);
        setPrefixInput("");
        setPostfixInput("");
        setSeparatorInput("");
        setCreateAdminUser(false);
        setAdminUsername("");
        setAdminEmail("");
        setAdminPassword("");
      }
      setTimeout(() => navigate("/company"), 1200);
    } catch (err: any) {
      if (err.response?.data?.error) setFormError(err.response.data.error);
      else setFormError("Failed to save company.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!isEditMode || !companyId) return;
    setDeleteLoading(true);
    setError("");
    try {
      await axios.delete(`${API_URL}/auth/company/${companyId}`);
      setSuccess("Deleted successfully!");
      setTimeout(() => navigate("/company"), 900);
    } catch {
      setError("Failed to delete company.");
    } finally {
      setDeleteLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  return (
    <div
      className={`${COLOR_GRADIENT} w-full max-w-full 2xl:max-w-[1440px] mx-auto shadow-2xl rounded-[2.4rem] p-0 sm:p-4 md:p-10 lg:p-16 xl:p-20  mt-12 border border-blue-100 animate__animated animate__fadeIn animate__faster`}
      style={{
        minHeight: "600px",
        boxShadow: "0 8px 32px 0 rgba(66,80,220,.08),0 0 0 1px #c7d1ff44",
        backdropFilter: "blur(2px)",
        backgroundAttachment: "fixed",
      }}
    >
      {/* HEADER */}
      <div className="py-6 px-4 md:px-10 flex flex-col sm:flex-row justify-between items-center">
        <div className="flex items-center gap-4">
          <span className="inline-flex rounded-full bg-gradient-to-tr from-blue-200 to-pink-200 p-4 shadow-lg drop-shadow-lg">
            <FaBuilding className="text-sky-600 text-4xl drop-shadow" />
          </span>
          <div>
            <h2 className="text-[2.2rem] font-black bg-gradient-to-r from-blue-700 to-fuchsia-700 inline-block text-transparent bg-clip-text drop-shadow-xl leading-tight tracking-tight">
              {isEditMode ? "Edit Company" : "Add Company"}
            </h2>
            <div className="mb-0 text-gray-500 font-medium text-base pt-1 ml-2">
              Add or modify your company profile with beautiful clarity & trust.
            </div>
          </div>
        </div>
      </div>

      {/* ALERT */}
      <div className="px-4 md:px-10" ref={alertRef}>
        {error && <Alert type="error" message={error} />}
        {formError && <Alert type="error" message={formError} />}
        {success && <Alert type="success" message={success} />}
      </div>

      {/* FORM CARD */}
      <div className="flex justify-center py-6 pb-16">
        <div className={`w-full bg-white/90 border border-blue-100 px-4 pt-8 pb-12 md:px-10 md:py-14 rounded-[2rem] shadow-2xl relative drop-shadow-xl max-w-full`}>
          {loading ? (
            <div className="flex justify-center items-center text-blue-700 font-semibold animate-pulse py-10 text-lg">
              <FaSpinner className="animate-spin mr-3 text-2xl" /> Loading company...
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              className="flex flex-col gap-10 animate__animated animate__fadeIn"
              autoComplete="off"
              style={{
                animation: "fadein .7s",
                transition: "background .2s",
              }}
            >
              {/* FORM FIELDS */}
              <div className="flex flex-col gap-5">
                {fieldGroups.map((group, idx) => (
                  <fieldset
                    key={group.title || idx}
                    className="rounded-2xl px-2 py-4 border border-slate-100 bg-gradient-to-r from-blue-50/40 to-fuchsia-50/30 shadow-sm mb-2"
                  >
                    {group.title && (
                      <legend className="font-semibold text-xl mb-4 text-blue-900 flex items-center gap-2 px-2">
                        {group.icon && <group.icon />}
                        <span>{group.title}</span>
                      </legend>
                    )}
                    <div className={`grid grid-cols-1 ${group.cols || ""} gap-x-8 gap-y-5`}>
                      {group.fields.map((fld: any) => {
                        if (fld.name === "password") {
                          // Special case: password field with show/hide
                          return (
                            <div key={fld.name} className={fld.gridFull ? "col-span-full" : ""}>
                              <FormField
                                label={fld.label}
                                name={fld.name}
                                required={fld.required}
                                value={company[fld.name as keyof typeof initialCompanyState] as string}
                                onChange={handleInputChange}
                                textarea={fld.textarea}
                                areaRows={fld.areaRows}
                                inputType={showPassword ? "text" : "password"}
                                icon={fld.icon}
                                showPasswordToggle={true}
                                showPassword={showPassword}
                                setShowPassword={setShowPassword}
                              />
                            </div>
                          );
                        } else if (fld.name === "country") {
                          return (
                            <div key={fld.name}>
                              <label className="block mb-1 text-sm font-medium text-blue-900 tracking-wide">Country</label>
                              <div className="flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full">
                                <select
                                  name="country"
                                  value={company.country}
                                  onChange={handleInputChange}
                                  className="flex-1 outline-none bg-transparent w-full"
                                >
                                  <option value="">-- Choose Country --</option>
                                  {Object.keys(locationMaster).map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        } else if (fld.name === "state") {
                          const states = company.country ? Object.keys(locationMaster[company.country] || {}) : [];
                          return (
                            <div key={fld.name}>
                              <label className="block mb-1 text-sm font-medium text-blue-900 tracking-wide">State</label>
                              <div className="flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full">
                                <select
                                  name="state"
                                  value={company.state}
                                  onChange={handleInputChange}
                                  className="flex-1 outline-none bg-transparent w-full"
                                  disabled={!company.country}
                                >
                                  <option value="">-- Choose State --</option>
                                  {states.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        } else if (fld.name === "city") {
                          const cities = (company.country && company.state) ? (locationMaster[company.country]?.[company.state] || []) : [];
                          return (
                            <div key={fld.name}>
                              <label className="block mb-1 text-sm font-medium text-blue-900 tracking-wide">City</label>
                              <div className="flex items-center bg-white rounded-lg border border-gray-300 px-3 py-2 focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-colors shadow-sm w-full">
                                <select
                                  name="city"
                                  value={company.city}
                                  onChange={handleInputChange}
                                  className="flex-1 outline-none bg-transparent w-full"
                                  disabled={!company.state}
                                >
                                  <option value="">-- Choose City --</option>
                                  {cities.map(c => (
                                    <option key={c} value={c}>{c}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          );
                        } else {
                          return (
                            <div key={fld.name} className={fld.gridFull ? "col-span-full" : ""}>
                              <FormField
                                label={fld.label}
                                name={fld.name}
                                required={fld.required}
                                value={company[fld.name as keyof typeof initialCompanyState] as string}
                                onChange={handleInputChange}
                                textarea={fld.textarea}
                                areaRows={fld.areaRows}
                                inputType={fld.inputType}
                                icon={fld.icon}
                              />
                            </div>
                          );
                        }
                      })}
                    </div>
                  </fieldset>
                ))}

                {/* Subscription Plan Details */}
                <fieldset className="rounded-2xl px-6 py-5 border border-indigo-100 bg-gradient-to-r from-indigo-50/40 via-white to-sky-50/30 shadow-sm mb-2">
                  <legend className="font-semibold text-xl mb-4 text-blue-900 flex items-center gap-2 px-2">
                    <span>Subscription Plan Details</span>
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_plan_id">
                        Select Plan
                      </label>
                      <select
                        id="subscription_plan_id"
                        value={company.subscription_plan_id || ""}
                        onChange={(e) => setCompany({ ...company, subscription_plan_id: e.target.value })}
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      >
                        <option value="">No Plan / Free</option>
                        {subscriptionPlans.map((p) => (
                          <option key={p.id} value={p.id}>
                            {p.name} (₹{p.price})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_start_date">
                        Start Date
                      </label>
                      <input
                        type="date"
                        id="subscription_start_date"
                        value={company.subscription_start_date || ""}
                        onChange={(e) => setCompany({ ...company, subscription_start_date: e.target.value })}
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm text-gray-800"
                      />
                    </div>
                    <div>
                      <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="subscription_status">
                        Status
                      </label>
                      <select
                        id="subscription_status"
                        value={company.subscription_status || "active"}
                        onChange={(e) => setCompany({ ...company, subscription_status: e.target.value })}
                        className="w-full border-2 border-slate-200 rounded-xl px-4 py-2.5 bg-white focus:border-blue-300 focus:outline-none text-sm font-semibold text-gray-800"
                      >
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="expired">Expired</option>
                      </select>
                    </div>
                  </div>
                </fieldset>

                {/* Optional Admin User Mapping */}
                {!isEditMode && (
                  <fieldset className="rounded-2xl px-6 py-5 border border-emerald-100 bg-gradient-to-r from-emerald-50/40 via-white to-teal-50/30 shadow-sm mb-2">
                    <legend className="font-semibold text-xl mb-4 text-emerald-950 flex items-center gap-2 px-2">
                      <span>Admin User Mapping</span>
                    </legend>
                    <div className="mb-4 flex items-center gap-3">
                      <input
                        type="checkbox"
                        id="create_admin_user"
                        checked={createAdminUser}
                        onChange={(e) => setCreateAdminUser(e.target.checked)}
                        className="w-5 h-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer"
                      />
                      <label htmlFor="create_admin_user" className="text-sm font-semibold text-slate-700 cursor-pointer select-none">
                        Create an administrator user mapped to this company
                      </label>
                    </div>

                    {createAdminUser && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 animate__animated animate__fadeIn animate__faster">
                        <div>
                          <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="admin_username">
                            Username
                          </label>
                          <input
                            type="text"
                            id="admin_username"
                            value={adminUsername}
                            onChange={(e) => setAdminUsername(e.target.value)}
                            placeholder="Admin username"
                            className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm text-gray-800"
                            required
                          />
                        </div>
                        <div>
                          <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="admin_email">
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="admin_email"
                            value={adminEmail}
                            onChange={(e) => setAdminEmail(e.target.value)}
                            placeholder="admin@company.com"
                            className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm text-gray-800"
                            required
                          />
                        </div>
                        <div>
                          <label className="block font-semibold text-blue-900/80 mb-2 uppercase tracking-wide text-xs" htmlFor="admin_password">
                            Password
                          </label>
                          <input
                            type="password"
                            id="admin_password"
                            value={adminPassword}
                            onChange={(e) => setAdminPassword(e.target.value)}
                            placeholder="Password"
                            className="w-full border-2 border-slate-200 rounded-xl px-4 py-2 bg-white focus:border-blue-300 focus:outline-none text-sm text-gray-800"
                            required
                          />
                        </div>
                      </div>
                    )}
                  </fieldset>
                )}

                {/* --- Prefix, Postfix, and Separator inputs --- */}
                <fieldset className="rounded-2xl px-2 py-4 border border-violet-50 bg-gradient-to-r from-white via-blue-50/30 to-fuchsia-50/20 shadow-sm mb-2">
                  <legend className="font-semibold text-base mb-2 text-sky-900 flex items-center gap-2 px-2 border-0">
                    Prefix, Postfix & Invoice Separator
                  </legend>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block font-bold mb-2 text-blue-900 text-base" htmlFor="prefix-input">
                        Prefix
                      </label>
                      <input
                        className="w-full p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-fuchsia-500 text-base font-medium transition bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
                        id="prefix-input"
                        name="prefix"
                        type="text"
                        value={prefixInput}
                        onChange={handlePrefixInputChange}
                        placeholder="Enter prefix"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="block font-bold mb-2 text-blue-900 text-base" htmlFor="postfix-input">
                        Postfix
                      </label>
                      <input
                        className="w-full p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-fuchsia-500 text-base font-medium transition bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
                        id="postfix-input"
                        name="postfix"
                        type="text"
                        value={postfixInput}
                        onChange={handlePostfixInputChange}
                        placeholder="Enter postfix"
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <label className="block font-bold mb-2 text-blue-900 text-base" htmlFor="separator-select">
                        Invoice Separator
                      </label>
                      <div className="flex flex-row gap-2">
                        <select
                          className="w-1/2 p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-sky-500 text-base font-medium bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
                          id="separator-select"
                          name="separator"
                          value={separatorInput}
                          onChange={handleSeparatorSelectChange}
                        >
                          <option value="">Choose...</option>
                          {separatorOptions.map(opt => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <input
                          className="w-1/2 p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-sky-500 text-base font-medium bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50"
                          name="separatorInput"
                          type="text"
                          maxLength={1}
                          value={separatorInput}
                          onChange={handleSeparatorInputChange}
                          placeholder="Other"
                          autoComplete="off"
                          style={{ letterSpacing: "4px" }}
                        />
                      </div>
                      <div className="text-xs pl-1 mt-0.5 text-gray-500 font-medium italic">
                        Choose how invoice parts are separated (e.g. <span className="font-bold">/</span>, <span className="font-bold">-</span>, <span className="font-bold">_</span>, <span className="font-bold">.</span>, <span className="font-bold">|</span>)
                      </div>
                    </div>
                  </div>
                </fieldset>
              </div>

              {/* SIGNATURE UPLOAD SECTION */}
              <fieldset className="rounded-2xl px-2 py-3 border border-blue-50 bg-gradient-to-r from-white via-blue-50/40 to-white shadow mb-2">
                <legend className="font-semibold text-base mb-2 text-blue-700 flex items-center gap-2 px-2 border-0">
                  <FaSignature /> Signature
                </legend>
                <div className="flex flex-col sm:flex-row gap-4 items-center">
                  <input
                    type="file"
                    accept="image/png, image/jpeg, image/jpg, image/webp, image/svg+xml"
                    id="signature"
                    name="signature"
                    onChange={handleSignatureChange}
                    className="block w-full sm:w-auto text-sm file:mr-4 file:py-2 file:px-5 file:rounded-xl file:border-0 
                              file:text-md file:font-semibold 
                              file:bg-blue-100 file:text-blue-700
                              hover:file:bg-blue-200
                              shadow"
                  />
                  {signaturePreview ? (
                    <div className="flex flex-row items-center gap-3">
                      <img
                        src={signaturePreview}
                        alt="Signature"
                        className="h-16 w-auto border-2 border-blue-200 bg-white rounded-lg shadow-lg"
                      />
                      <button
                        type="button"
                        aria-label="Remove signature"
                        className="ml-1 text-red-600 hover:text-white hover:bg-red-600 border border-red-200 px-3 py-2 rounded-md font-semibold shadow-md duration-150 transition-all"
                        onClick={clearSignature}
                      >
                        <FaTimes className="inline" /> <span className="sr-only md:not-sr-only">Remove</span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mt-1 pl-0.5 font-medium italic">
                      Upload your company's authorized signature image (jpg, jpeg, png, svg, webp).
                    </div>
                  )}
                </div>
              </fieldset>

              {/* Company Letterhead */}
              <fieldset className="rounded-2xl mb-2 px-2 py-5 border border-blue-50 bg-gradient-to-r from-blue-50/60 to-white/70 shadow-sm">
                <legend className="font-semibold text-base mb-3 text-sky-900 flex items-center gap-2 px-2 border-0">
                  Company Letterhead
                </legend>
                <div className="flex flex-row flex-wrap gap-4 items-center pl-2">
                  <input
                    type="file"
                    id="letterhead"
                    name="letterhead"
                    onChange={handleLetterheadChange}
                    className="block w-full sm:w-auto text-sm file:mr-4 file:py-2 file:px-5 file:rounded-xl file:border-0 
                              file:text-md file:font-semibold 
                              file:bg-blue-100 file:text-blue-700
                              hover:file:bg-blue-200
                              shadow"
                  />
                  {letterheadPreview ? (
                    <div className="flex flex-row items-center gap-3">
                      <img
                        src={letterheadPreview}
                        alt="Letterhead Preview"
                        className="h-16 w-auto border-2 border-blue-200 bg-white rounded-lg shadow-lg object-contain"
                      />
                      <button
                        type="button"
                        aria-label="Remove letterhead"
                        className="ml-1 text-red-600 hover:text-white hover:bg-red-600 border border-red-200 px-3 py-2 rounded-md font-semibold shadow-md duration-150 transition-all"
                        onClick={clearLetterhead}
                      >
                        <FaTimes className="inline" /> <span className="sr-only md:not-sr-only">Remove</span>
                      </button>
                    </div>
                  ) : (
                    <div className="text-xs text-gray-400 mt-1 pl-0.5 font-medium italic">
                      Upload your company's letterhead image (jpg, jpeg, png, svg, webp) to use in quotations.
                    </div>
                  )}
                </div>
              </fieldset>

              {/* HSN/SAC */}
              <fieldset className="rounded-2xl mb-2 px-2 py-5 border border-blue-50 bg-gradient-to-r from-blue-50/60 to-white/70 shadow-sm">
                <legend className="font-semibold text-base mb-3 text-sky-900 flex items-center gap-2 px-2 border-0">
                  HSN/SAC
                </legend>
                <div className="flex flex-row flex-wrap gap-2 items-center">
                  <input
                    type="text"
                    className="w-32 p-3 border border-blue-200 rounded-xl focus:outline-none ring-blue-300 focus:ring-2 transition font-medium bg-white/50 shadow-sm"
                    value={hsnInput}
                    onChange={handleHsnInputChange}
                    placeholder="HSN/SAC number"
                  />
                  <input
                    type="text"
                    className="w-40 p-3 border border-blue-100 rounded-xl focus:outline-none ring-blue-200 focus:ring-2 transition font-medium bg-white/50 shadow-sm"
                    value={hsnDescInput}
                    onChange={handleHsnDescInputChange}
                    placeholder="Add description (optional)"
                  />
                  <button
                    type="button"
                    className="bg-gradient-to-br from-blue-500 to-pink-400 hover:from-blue-600 hover:to-pink-500 text-white px-6 py-2 rounded-xl shadow font-bold flex items-center gap-2 transition"
                    onClick={addHsnSac}
                    disabled={!hsnInput.trim()}
                    tabIndex={0}
                  >
                    <FaPlus /> Add
                  </button>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {hsnSacs.map((val, idx) => (
                      <span
                        key={idx}
                        className="bg-blue-100/90 border border-blue-200 text-blue-800 px-4 py-1 rounded-full flex items-center gap-2 text-[1em] font-semibold shadow"
                      >
                        {val}
                        <button
                          type="button"
                          className="ml-1 text-blue-700 hover:text-pink-500 rounded-full p-0.5"
                          onClick={() => removeHsnSac(val)}
                          aria-label="Remove HSN"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </fieldset>
              {/* GST SLAB */}
              <fieldset className="rounded-2xl mb-2 px-2 py-5 border border-fuchsia-50 bg-gradient-to-r from-white via-fuchsia-50/40 to-white/80 shadow-sm">
                <legend className="font-semibold text-base mb-3 text-fuchsia-900 flex items-center gap-2 px-2 border-0">
                  GST SLAB
                </legend>
                <div className="flex flex-row flex-wrap gap-2 items-center">
                  <input
                    type="text"
                    className="w-32 p-3 border border-fuchsia-200 rounded-xl focus:outline-none ring-fuchsia-300 focus:ring-2 font-medium bg-white/50 shadow-sm"
                    value={gstInput}
                    onChange={handleGstInputChange}
                    placeholder="GST Slab"
                  />
                  <input
                    type="text"
                    className="w-40 p-3 border border-fuchsia-100 rounded-xl focus:outline-none ring-fuchsia-200 focus:ring-2 font-medium bg-white/50 shadow-sm"
                    value={gstDescInput}
                    onChange={handleGstDescInputChange}
                    placeholder="Add description (optional)"
                  />
                  <button
                    type="button"
                    className="bg-gradient-to-br from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white px-6 py-2 rounded-xl shadow font-bold flex items-center gap-2 transition"
                    onClick={addGstSlab}
                    disabled={!gstInput.trim()}
                  >
                    <FaPlus /> Add
                  </button>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {gstSlabs.map((val, idx) => (
                      <span
                        key={idx}
                        className="bg-fuchsia-100/90 border border-fuchsia-200 text-fuchsia-800 px-4 py-1 rounded-full flex items-center gap-2 text-[1em] font-semibold shadow"
                      >
                        {val}
                        <button
                          type="button"
                          className="ml-1 text-fuchsia-700 hover:text-pink-500 rounded-full p-0.5"
                          onClick={() => removeGstSlab(val)}
                          aria-label="Remove GST"
                        >
                          <FaTimes />
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </fieldset>

              {/* CTA BUTTONS */}
              <div className="flex flex-wrap gap-4 pt-6 justify-between items-center">
                <button
                  type="submit"
                  className="flex items-center gap-3 px-10 py-4 rounded-xl shadow-2xl font-black bg-gradient-to-tr from-blue-600 to-fuchsia-700 text-white text-xl transition active:scale-95 hover:ring-2 hover:ring-blue-200 focus:outline-none focus:ring-2 focus:ring-pink-200"
                  disabled={submitting}
                >
                  {submitting ? <FaSpinner className="animate-spin inline" /> : ""}
                  {isEditMode
                    ? submitting
                      ? "Updating..."
                      : "Update Company"
                    : submitting
                    ? "Adding..."
                    : "Add Company"}
                </button>
                <button
                  type="button"
                  className="bg-gradient-to-r from-slate-200 to-blue-100 px-8 py-3 rounded-xl border border-gray-300 hover:from-blue-100 hover:to-fuchsia-50 font-bold text-lg text-slate-700 transition flex items-center gap-3 shadow-md"
                  onClick={() => navigate("/company")}
                  disabled={submitting || deleteLoading}
                >
                  <FaArrowLeft className="text-blue-600" /> Cancel
                </button>

                {isEditMode && (
                  <button
                    type="button"
                    className="ml-auto flex items-center px-8 py-3 bg-gradient-to-br from-rose-600 to-pink-600 text-white rounded-xl font-bold text-lg shadow-xl hover:scale-105 transition gap-3"
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={deleteLoading}
                  >
                    <FaTrashAlt />
                    {deleteLoading ? "Deleting..." : "Delete"}
                  </button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>

      {/* DELETE CONFIRM MODAL */}
      {showDeleteConfirm && (
        <DeleteConfirm
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
          loading={deleteLoading}
          message="Are you sure you want to delete this company?"
        />
      )}
    </div>
  );
};

// --- Alert Component
const Alert: React.FC<{ type: "success" | "error"; message: string }> = ({
  type,
  message,
}) => {
  const icon =
    type === "success" ? (
      <FaCheckCircle className="mr-2 text-green-600 text-lg" />
    ) : (
      <FaExclamationCircle className="mr-2 text-red-600 text-lg" />
    );
  const color =
    type === "success"
      ? "bg-gradient-to-r from-green-50 to-green-100 text-green-700 border-green-200"
      : "bg-gradient-to-r from-red-50 to-rose-100 text-red-700 border-red-200";
  return (
    <div
      className={`mb-4 rounded-2xl border px-5 py-3 flex items-center shadow font-bold ${color} animate__animated animate__fadeIn`}
    >
      {icon}
      {message}
    </div>
  );
};

// --- Modern FormField with beautiful full-width, support for 1-2-3 column layout
interface FormFieldProps {
  label: string;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  textarea?: boolean;
  areaRows?: number;
  inputType?: string;
  required?: boolean;
  icon?: React.ReactNode;
  showPasswordToggle?: boolean;
  showPassword?: boolean;
  setShowPassword?: React.Dispatch<React.SetStateAction<boolean>>;
}

const FormField: React.FC<FormFieldProps> = ({
  label,
  name,
  value,
  onChange,
  textarea = false,
  areaRows = 2,
  inputType = "text",
  required = false,
  icon = null,
  showPasswordToggle,
  showPassword,
  setShowPassword,
}) => {
  const isPasswordType = inputType === "password" || inputType === "text";
  return (
    <div className="flex flex-col relative group">
      <label
        className="block font-bold mb-2 text-blue-900 flex items-center gap-2 text-lg tracking-tight"
        htmlFor={name}
      >
        {icon}
        {label}
        {required && <span className="text-rose-600 ml-1">*</span>}
      </label>
      {textarea ? (
        <textarea
          className="w-full p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-blue-500 bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50 text-base font-medium resize-none transition ring-0"
          id={name}
          name={name}
          rows={areaRows}
          value={value}
          onChange={onChange}
          required={required}
          spellCheck
          placeholder={`Enter ${label.toLowerCase()}...`}
        />
      ) : (
        <div className="relative">
          <input
            className="w-full p-4 border border-blue-200 rounded-xl shadow-md focus:outline-none focus:border-fuchsia-500 text-base font-medium transition bg-gradient-to-tr from-white via-blue-50 to-fuchsia-50 pr-12"
            id={name}
            name={name}
            type={
              showPasswordToggle
                ? showPassword
                  ? "text"
                  : "password"
                : inputType
            }
            required={required}
            value={value}
            onChange={onChange}
            autoComplete={
              showPasswordToggle
                ? "new-password"
                : inputType === "password"
                ? "new-password"
                : "off"
            }
            placeholder={`Enter ${label.toLowerCase()}${required ? " *" : ""}`}
          />
          {showPasswordToggle && setShowPassword && (
            <button
              type="button"
              aria-label={showPassword ? "Hide password" : "Show password"}
              className="absolute right-4 top-1/2 transform -translate-y-1/2 text-sky-600 hover:text-fuchsia-600 transition"
              tabIndex={-1}
              onClick={() => setShowPassword((p) => !p)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </button>
          )}
        </div>
      )}
      {/* Gradient line animation */}
      <span className="absolute bottom-0 left-1/2 w-0 h-1 bg-gradient-to-r from-blue-400 to-fuchsia-500 transition-all duration-300 group-focus-within:w-full group-focus-within:left-0" />
    </div>
  );
};

// --- Delete Confirm Component (modal)
const DeleteConfirm: React.FC<{
  onConfirm: () => void;
  onCancel: () => void;
  loading: boolean;
  message?: string;
}> = ({ onConfirm, onCancel, loading, message }) => (
  <div className="fixed inset-0 flex items-center justify-center z-[120] bg-gradient-to-b from-black/30 to-pink-50/90 animate__animated animate__fadeIn">
    <div className="bg-white p-7 md:p-12 rounded-3xl shadow-2xl border border-blue-200 relative w-full max-w-xs">
      <div className="mb-8 text-2xl font-bold text-red-700 flex items-center gap-2">
        <FaExclamationCircle className="text-3xl" />
        {message || "Are you sure you want to delete?"}
      </div>
      <div className="flex gap-3 justify-end">
        <button
          onClick={onCancel}
          disabled={loading}
          className="px-6 py-2.5 bg-gray-200 hover:bg-gray-300 rounded-xl font-bold text-gray-700 mr-2 shadow"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="px-7 py-2.5 bg-gradient-to-br from-red-600 to-pink-500 text-white rounded-xl font-black shadow-lg hover:brightness-110 transition"
        >
          {loading ? <FaSpinner className="animate-spin inline" /> : "Delete"}
        </button>
      </div>
    </div>
  </div>
);

export default AddCompany;